/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import Sisense from '@ion/core/src/services/Sisense';
import {SisenseState} from '@ion/core/src/services/SisenseUtils';

// [TO-DO]: We need to add all Sisense states and getter functions as `serverOptions` added
// the mapping for the Sisense local state and it's getter functions...
const stateMap = {
  [SisenseState.ServerOptions]       : Sisense.getServerOptions,
  [SisenseState.SisenseOptionsError] : Sisense.getSisenseOptionsError,
  [SisenseState.SisenseLoading]      : Sisense.isLoading,
  [SisenseState.SessionCreated]      : Sisense.isSessionCreated
};

/**
 * The custom hook useSisenseSelector to select state from Sisense store.
 * 
 * ```typescript
 * 
 *  import {useSisenseSelector} from '@applications/src/sisense/hooks';
 * 
 *  const serverOptions = useSisenseSelector('serverOptions');
 * 
 * ```
 * @param stateKey The key corresponding to the state in the store.
 * @returns The selected state.
 */
export const useSisenseSelector = (stateKey: SisenseState, initialState?: any) => {
  const [state, setState] = useState(initialState ?? stateMap[stateKey]?.());

  useEffect(() => {
    const onStoreUpdate = () => {
      const newValue = stateMap[stateKey]?.();
      setState(newValue);
    };

    // Subscribe to Sisense store updates
    Sisense.subscribe(onStoreUpdate);

    return () => {
      // Cleanup function to unsubscribe from store updates when component unmounts
      Sisense.unsubscribe(onStoreUpdate);
    };
  }, []);

  return state;
};

export default useSisenseSelector;
